import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Typography,
  Box,
  Switch,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Alert
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Business as BusinessIcon
} from '@mui/icons-material';
import * as apiIntegrationsService from '../../services/apiIntegrationsService';
import FacilityAssignmentTree from '../common/FacilityAssignmentTree';

const APIIntegrationForm = ({ open, integration, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    type: 'REST',
    category: 'Healthcare',
    baseUrl: '',
    version: '',
    active: true,
    authType: 'none',
    authConfig: {
      username: '',
      password: '',
      token: '',
      apiKey: '',
      apiKeyHeader: 'X-API-Key',
      clientId: '',
      clientSecret: '',
      tokenUrl: '',
      scope: ''
    },
    timeout: 5000,
    retryAttempts: 3,
    retryDelay: 1000,
    rateLimit: {
      requests: 100,
      window: 60000
    },
    defaultHeaders: {},
    defaultParams: {},
    environment: 'development',
    enableLogging: true,
    enableMetrics: true,
    healthCheckUrl: '',
    healthCheckInterval: 15,
    vendor: '',
    documentation: '',
    supportContact: '',
    tags: [],
    notes: '',
    assignedFacilities: []
  });

  const [errors, setErrors] = useState({});
  const [newTag, setNewTag] = useState('');

  useEffect(() => {
    if (integration) {
      setFormData(prev => ({
        ...prev,
        ...integration,
        authConfig: {
          ...prev.authConfig,
          ...integration.authConfig
        },
        rateLimit: {
          ...prev.rateLimit,
          ...integration.rateLimit
        },
        defaultHeaders: integration.defaultHeaders || {},
        defaultParams: integration.defaultParams || {},
        tags: integration.tags || [],
        assignedFacilities: integration.assignedFacilities || []
      }));
    } else {
      // Reset form for new integration
      setFormData({
        name: '',
        description: '',
        type: 'REST',
        category: 'Healthcare',
        baseUrl: '',
        version: '',
        active: true,
        authType: 'none',
        authConfig: {
          username: '',
          password: '',
          token: '',
          apiKey: '',
          apiKeyHeader: 'X-API-Key',
          clientId: '',
          clientSecret: '',
          tokenUrl: '',
          scope: ''
        },
        timeout: 5000,
        retryAttempts: 3,
        retryDelay: 1000,
        rateLimit: {
          requests: 100,
          window: 60000
        },
        defaultHeaders: {},
        defaultParams: {},
        environment: 'development',
        enableLogging: true,
        enableMetrics: true,
        healthCheckUrl: '',
        healthCheckInterval: 15,
        vendor: '',
        documentation: '',
        supportContact: '',
        tags: [],
        notes: '',
        assignedFacilities: []
      });
    }
    setErrors({});
  }, [integration, open]);

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const handleNestedChange = (parent, field, value) => {
    setFormData(prev => ({
      ...prev,
      [parent]: {
        ...prev[parent],
        [field]: value
      }
    }));
  };

  const handleAddTag = () => {
    if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
      handleChange('tags', [...formData.tags, newTag.trim()]);
      setNewTag('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    handleChange('tags', formData.tags.filter(tag => tag !== tagToRemove));
  };

  const validateForm = () => {
    const validation = apiIntegrationsService.validateIntegration(formData);
    setErrors(validation.errors.reduce((acc, error) => {
      const field = error.toLowerCase().includes('name') ? 'name' :
                   error.toLowerCase().includes('url') ? 'baseUrl' :
                   error.toLowerCase().includes('type') ? 'type' :
                   error.toLowerCase().includes('category') ? 'category' :
                   'general';
      acc[field] = error;
      return acc;
    }, {}));
    
    return validation.valid;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  const renderAuthFields = () => {
    switch (formData.authType) {
      case 'basic':
        return (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Username"
                value={formData.authConfig.username}
                onChange={(e) => handleNestedChange('authConfig', 'username', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Password"
                type="password"
                value={formData.authConfig.password}
                onChange={(e) => handleNestedChange('authConfig', 'password', e.target.value)}
              />
            </Grid>
          </Grid>
        );

      case 'bearer':
        return (
          <TextField
            fullWidth
            label="Bearer Token"
            value={formData.authConfig.token}
            onChange={(e) => handleNestedChange('authConfig', 'token', e.target.value)}
          />
        );

      case 'api_key':
        return (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={8}>
              <TextField
                fullWidth
                label="API Key"
                value={formData.authConfig.apiKey}
                onChange={(e) => handleNestedChange('authConfig', 'apiKey', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Header Name"
                value={formData.authConfig.apiKeyHeader}
                onChange={(e) => handleNestedChange('authConfig', 'apiKeyHeader', e.target.value)}
                placeholder="X-API-Key"
              />
            </Grid>
          </Grid>
        );

      case 'oauth2':
        return (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Client ID"
                value={formData.authConfig.clientId}
                onChange={(e) => handleNestedChange('authConfig', 'clientId', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Client Secret"
                type="password"
                value={formData.authConfig.clientSecret}
                onChange={(e) => handleNestedChange('authConfig', 'clientSecret', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Token URL"
                value={formData.authConfig.tokenUrl}
                onChange={(e) => handleNestedChange('authConfig', 'tokenUrl', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Scope"
                value={formData.authConfig.scope}
                onChange={(e) => handleNestedChange('authConfig', 'scope', e.target.value)}
              />
            </Grid>
          </Grid>
        );

      default:
        return (
          <Alert severity="info">
            No authentication required for this integration.
          </Alert>
        );
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      fullWidth 
      maxWidth="md"
      PaperProps={{
        sx: { height: '90vh' }
      }}
    >
      <DialogTitle>
        {integration ? 'Edit API Integration' : 'Create API Integration'}
      </DialogTitle>
      
      <DialogContent dividers sx={{ p: 3 }}>
        {errors.general && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errors.general}
          </Alert>
        )}

        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">Basic Information</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Integration Name *"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  error={!!errors.name}
                  helperText={errors.name}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Version"
                  value={formData.version}
                  onChange={(e) => handleChange('version', e.target.value)}
                  placeholder="v1.0, 2.1, etc."
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description *"
                  multiline
                  rows={2}
                  value={formData.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                  error={!!errors.description}
                  helperText={errors.description}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                  <InputLabel>API Type *</InputLabel>
                  <Select
                    value={formData.type}
                    label="API Type *"
                    onChange={(e) => handleChange('type', e.target.value)}
                    error={!!errors.type}
                  >
                    {apiIntegrationsService.getAPITypes().map(type => (
                      <MenuItem key={type} value={type}>{type}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                  <InputLabel>Category *</InputLabel>
                  <Select
                    value={formData.category}
                    label="Category *"
                    onChange={(e) => handleChange('category', e.target.value)}
                    error={!!errors.category}
                  >
                    {apiIntegrationsService.getIntegrationCategories().map(category => (
                      <MenuItem key={category} value={category}>{category}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                  <InputLabel>Environment</InputLabel>
                  <Select
                    value={formData.environment}
                    label="Environment"
                    onChange={(e) => handleChange('environment', e.target.value)}
                  >
                    <MenuItem value="development">Development</MenuItem>
                    <MenuItem value="staging">Staging</MenuItem>
                    <MenuItem value="production">Production</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Base URL *"
                  value={formData.baseUrl}
                  onChange={(e) => handleChange('baseUrl', e.target.value)}
                  error={!!errors.baseUrl}
                  helperText={errors.baseUrl}
                  placeholder="https://api.example.com"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Vendor"
                  value={formData.vendor}
                  onChange={(e) => handleChange('vendor', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.active}
                      onChange={(e) => handleChange('active', e.target.checked)}
                    />
                  }
                  label="Active"
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">Authentication</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Authentication Type</InputLabel>
                  <Select
                    value={formData.authType}
                    label="Authentication Type"
                    onChange={(e) => handleChange('authType', e.target.value)}
                  >
                    {apiIntegrationsService.getAuthenticationTypes().map(type => (
                      <MenuItem key={type} value={type}>
                        {type === 'none' ? 'No Authentication' :
                         type === 'basic' ? 'Basic Authentication' :
                         type === 'bearer' ? 'Bearer Token' :
                         type === 'api_key' ? 'API Key' :
                         type === 'oauth2' ? 'OAuth 2.0' :
                         type === 'certificate' ? 'Certificate' : type}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                {renderAuthFields()}
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">Configuration</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Timeout (ms)"
                  type="number"
                  value={formData.timeout}
                  onChange={(e) => handleChange('timeout', parseInt(e.target.value) || 5000)}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Retry Attempts"
                  type="number"
                  value={formData.retryAttempts}
                  onChange={(e) => handleChange('retryAttempts', parseInt(e.target.value) || 3)}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Retry Delay (ms)"
                  type="number"
                  value={formData.retryDelay}
                  onChange={(e) => handleChange('retryDelay', parseInt(e.target.value) || 1000)}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Health Check URL"
                  value={formData.healthCheckUrl}
                  onChange={(e) => handleChange('healthCheckUrl', e.target.value)}
                  placeholder="Leave empty to use base URL"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.enableLogging}
                      onChange={(e) => handleChange('enableLogging', e.target.checked)}
                    />
                  }
                  label="Enable Logging"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.enableMetrics}
                      onChange={(e) => handleChange('enableMetrics', e.target.checked)}
                    />
                  }
                  label="Enable Metrics"
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <BusinessIcon />
              <Typography variant="h6">Facilities</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Select which facilities can use this API integration. Only selected facilities will have access to this integration.
            </Typography>
            <FacilityAssignmentTree
              selectedFacilities={formData.assignedFacilities}
              onChange={(facilityIds) => handleChange('assignedFacilities', facilityIds)}
              showSummary={true}
              maxHeight={300}
            />
          </AccordionDetails>
        </Accordion>

        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">Additional Information</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Documentation URL"
                  value={formData.documentation}
                  onChange={(e) => handleChange('documentation', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Support Contact"
                  value={formData.supportContact}
                  onChange={(e) => handleChange('supportContact', e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ mb: 2 }}>
                  <Grid container spacing={1} alignItems="center">
                    <Grid item xs={10}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Add Tag"
                        value={newTag}
                        onChange={(e) => setNewTag(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && handleAddTag()}
                      />
                    </Grid>
                    <Grid item xs={2}>
                      <Button
                        fullWidth
                        variant="outlined"
                        startIcon={<AddIcon />}
                        onClick={handleAddTag}
                        disabled={!newTag.trim()}
                      >
                        Add
                      </Button>
                    </Grid>
                  </Grid>
                </Box>
                <Box>
                  {formData.tags.map((tag) => (
                    <Chip
                      key={tag}
                      label={tag}
                      onDelete={() => handleRemoveTag(tag)}
                      deleteIcon={<DeleteIcon />}
                      sx={{ m: 0.5 }}
                    />
                  ))}
                </Box>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Notes"
                  multiline
                  rows={3}
                  value={formData.notes}
                  onChange={(e) => handleChange('notes', e.target.value)}
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained">
          {integration ? 'Update' : 'Create'} Integration
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default APIIntegrationForm; 